import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ParameterForm } from './ParameterForm';
import type { AgentParameterDto, CreateAgentParameterRequest, UpdateAgentParameterRequest } from '../../types/agent';

interface ParameterListProps {
  parameters: AgentParameterDto[];
  onAdd: (parameter: CreateAgentParameterRequest) => void;
  onUpdate: (id: string, parameter: UpdateAgentParameterRequest) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
}

/**
 * Parameter list component for managing agent parameters.
 */
export const ParameterList = ({
  parameters,
  onAdd,
  onUpdate,
  onDelete,
  readOnly = false,
}: ParameterListProps) => {
  const [openForm, setOpenForm] = useState(false);
  const [editingParameter, setEditingParameter] = useState<AgentParameterDto | null>(null);

  const handleAdd = () => {
    setEditingParameter(null);
    setOpenForm(true);
  };

  const handleEdit = (parameter: AgentParameterDto) => {
    setEditingParameter(parameter);
    setOpenForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this parameter?')) {
      onDelete(id);
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingParameter(null);
  };

  const handleFormSubmit = (data: CreateAgentParameterRequest | UpdateAgentParameterRequest) => {
    if (editingParameter) {
      onUpdate(editingParameter.id, data as UpdateAgentParameterRequest);
    } else {
      onAdd(data as CreateAgentParameterRequest);
    }
    handleFormClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Parameters</Typography>
        {!readOnly && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Parameter
          </Button>
        )}
      </Box>

      {parameters.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No parameters defined. {!readOnly && 'Click "Add Parameter" to create one.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Default Value</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Secret</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param) => (
                <TableRow key={param.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {param.key}
                    </Typography>
                  </TableCell>
                  <TableCell>{param.label}</TableCell>
                  <TableCell>
                    <Chip label={param.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {param.isSecret ? '••••••••' : param.defaultValue || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {param.isRequired && <Chip label="Required" size="small" color="primary" />}
                  </TableCell>
                  <TableCell>
                    {param.isSecret && <Chip label="Secret" size="small" color="warning" />}
                  </TableCell>
                  <TableCell align="right">
                    {!readOnly && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(param)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(param.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openForm && (
        <ParameterForm
          parameter={editingParameter}
          open={openForm}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </Box>
  );
};
